import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bot,
  BrainCircuit,
  FileStack,
  ShieldCheck,
  Users,
} from "lucide-react";
import config from "../config/config";
import CockpitShell from "../components/CockpitShell";
import AdminB1Workspace from "../components/admin/AdminB1Workspace";
import AdminErrorBoundary from "../components/admin/AdminErrorBoundary";
import PlatformAccessAdminPanel from "../components/admin/PlatformAccessAdminPanel";
import { Button } from "../components/ui/button";
import { buildAuthHeaders, buildAuthRequestConfig } from "../utils/authSession";

const navItems = [
  { href: "/dashboard/admin", label: "Command", icon: BrainCircuit },
  { href: "/dashboard/admin/assessment-ops", label: "Assessment", icon: FileStack },
  { href: "/dashboard/admin/users", label: "Users", icon: Users },
  { href: "/dashboard/security", label: "Security", icon: ShieldCheck },
  { href: "/dashboard/gpt", label: "GPT", icon: Bot },
];

const platformSignals = [
  { label: "System", value: "Live", note: "Health und Rollenpfade bleiben sichtbar, aber nachgeordnet." },
  { label: "Ops Fokus", value: "Review", note: "Review Queue bleibt die erste Admin-Arbeitsflaeche." },
  { label: "Scope", value: "B1", note: "B1-Ops fuehren den lokalen Arbeitsmodus." },
];

function noCacheParams(params = {}) {
  return { ...params, _ts: Date.now() };
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ total: 0 });
  const [health, setHealth] = useState({ ok: false, name: "Experts4Europe" });
  const [languageHealth, setLanguageHealth] = useState({ summary: null, modules: [] });
  const [languagePool, setLanguagePool] = useState([]);
  const [languageBatches, setLanguageBatches] = useState([]);
  const [languageAttempts, setLanguageAttempts] = useState([]);
  const [languageOpsState, setLanguageOpsState] = useState({ loading: true, error: "", busy: "" });
  const [generatorForm, setGeneratorForm] = useState({ module_key: "reading", count: 3, focus_text: "" });

  useEffect(() => {
    document.title = "Experts4Europe | Admin Dashboard";
  }, []);

  const authHeaders = buildAuthHeaders();

  const loadLanguageOps = async (headers) => {
    setLanguageOpsState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const [healthRes, poolRes, batchRes, attemptsRes] = await Promise.all([
        axios.get(
          `${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool/health`,
          buildAuthRequestConfig({ headers, params: noCacheParams() })
        ),
        axios.get(`${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool`, {
          ...buildAuthRequestConfig({ headers }),
          params: noCacheParams({ status: "review_pending", limit: 12 }),
        }),
        axios.get(`${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool/batches`, {
          ...buildAuthRequestConfig({ headers }),
          params: noCacheParams({ limit: 8 }),
        }),
        axios.get(`${config.API_BASE_URL}/api/admin/language-tests/b1/attempts`, {
          ...buildAuthRequestConfig({ headers }),
          params: noCacheParams({ limit: 8 }),
        }),
      ]);
      setLanguageHealth({
        summary: healthRes.data?.summary || null,
        modules: Array.isArray(healthRes.data?.modules) ? healthRes.data.modules : [],
      });
      setLanguagePool(Array.isArray(poolRes.data?.rows) ? poolRes.data.rows : []);
      setLanguageBatches(Array.isArray(batchRes.data?.rows) ? batchRes.data.rows : []);
      setLanguageAttempts(Array.isArray(attemptsRes.data?.rows) ? attemptsRes.data.rows : []);
      setLanguageOpsState((current) => ({ ...current, loading: false, error: "" }));
    } catch (_err) {
      setLanguageOpsState((current) => ({
        ...current,
        loading: false,
        error: "Die B1-Pool- und Review-Daten konnten nicht geladen werden.",
      }));
      setLanguageHealth({ summary: null, modules: [] });
      setLanguagePool([]);
      setLanguageBatches([]);
      setLanguageAttempts([]);
    }
  };

  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/api/health`).then((res) => setHealth(res.data || {})).catch(() => {});
    axios.get(
      `${config.API_BASE_URL}/api/users/summary`,
      buildAuthRequestConfig({
        headers: authHeaders,
        params: noCacheParams({ page: 1, limit: 1 }),
      })
    ).then((res) => setSummary(res.data || { total: 0 })).catch(() => setSummary({ total: 0 }));
    loadLanguageOps(authHeaders);
  }, []);

  const handleGenerateBatch = async () => {
    setLanguageOpsState((current) => ({ ...current, busy: "generate", error: "" }));
    try {
      await axios.post(
        `${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool/generate`,
        generatorForm,
        buildAuthRequestConfig({ headers: authHeaders })
      );
      await loadLanguageOps(authHeaders);
      setGeneratorForm((current) => ({ ...current, focus_text: "" }));
      setLanguageOpsState((current) => ({ ...current, busy: "" }));
    } catch (_err) {
      setLanguageOpsState((current) => ({ ...current, busy: "", error: "Der Generierungsbatch konnte nicht gestartet werden." }));
    }
  };

  const handleReviewItem = async (itemId, decision) => {
    setLanguageOpsState((current) => ({ ...current, busy: `review:${itemId}`, error: "" }));
    try {
      await axios.post(`${config.API_BASE_URL}/api/admin/language-tests/b1/item-pool/${itemId}/review`, {
        decision,
        review_note: decision === "approve" ? "Direkt im Admin-Dashboard freigegeben." : "Direkt im Admin-Dashboard abgelehnt.",
      }, buildAuthRequestConfig({ headers: authHeaders }));
      await loadLanguageOps(authHeaders);
      setLanguageOpsState((current) => ({ ...current, busy: "" }));
    } catch (_err) {
      setLanguageOpsState((current) => ({ ...current, busy: "", error: "Der Review-Status konnte nicht gespeichert werden." }));
    }
  };

  const languageSummary = {
    pending: languageHealth.summary?.pending_count ?? languagePool.length,
    approved: languageHealth.summary?.approved_count ?? 0,
    fallbackModules: Array.isArray(languageHealth.summary?.fallback_needed_modules) ? languageHealth.summary.fallback_needed_modules : [],
  };

  return (
    <CockpitShell
      title="AI Operations Room"
      subtitle="Review first bleibt die Regel. B1-Ops, Guardrails und Systemsignale liegen in einer konzentrierten Operations-Struktur statt in mehreren konkurrierenden Dashboards."
      badge="Experts4Europe Admin"
      navItems={navItems}
      wideLayout
      theme="admin"
      shellLabel="AI Operations Room"
      shellDescription="Ein gemeinsames Ops-Template fuer Review, Audit, Generierung und kompakte Plattformsignale."
      modeLabel="Review First"
      liveLabel="Admin"
      utilityHref={null}
      topSignal="Admin System"
      topContext="AI Operations Room"
      actions={
        <>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/admin/users")}>
            User Ops
          </Button>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/security")}>
            Security
          </Button>
          <Button className="dashboard-action-button" onClick={() => navigate("/dashboard/gpt")}>
            GPT Control
          </Button>
        </>
      }
    >
      <div className="dashboard-body">
        <div className="dashboard-main-stage">
          <section className="dashboard-mission-board">
            <div className="dashboard-section-head">
              <div>
                <span className="dashboard-kicker">Review Desk</span>
                <h2 className="dashboard-section-title">Review zuerst. Generierung und Batches danach.</h2>
                <p className="dashboard-section-copy">Eine dominante Arbeitsbuehne fuer den B1-Betrieb mit lokaler Subnavigation und auditierbaren Guardrails.</p>
              </div>
            </div>

            <div className="dashboard-summary-grid dashboard-summary-grid-admin">
              <article className="dashboard-summary-card dashboard-summary-card-highlight">
                <span className="dashboard-summary-label">Review offen</span>
                <strong>{languageSummary.pending}</strong>
                <p>Offene Queue bleibt die erste operative Entscheidungsebene.</p>
              </article>
              <article className="dashboard-summary-card">
                <span className="dashboard-summary-label">Freigegebene Basis</span>
                <strong>{languageSummary.approved}</strong>
                <p>Nur diese Items duerfen in echte Candidate-Attempts.</p>
              </article>
              <article className="dashboard-summary-card">
                <span className="dashboard-summary-label">Fallback-Druck</span>
                <strong>{languageSummary.fallbackModules.length}</strong>
                <p>{languageSummary.fallbackModules.length ? languageSummary.fallbackModules.join(", ") : "Keine Module im Fallback."}</p>
              </article>
            </div>

            <AdminErrorBoundary>
              <PlatformAccessAdminPanel />
            </AdminErrorBoundary>

            <AdminB1Workspace
              languageHealth={languageHealth}
              languagePool={languagePool}
              languageBatches={languageBatches}
              languageAttempts={languageAttempts}
              languageOpsState={languageOpsState}
              generatorForm={generatorForm}
              setGeneratorForm={setGeneratorForm}
              handleGenerateBatch={handleGenerateBatch}
              handleReviewItem={handleReviewItem}
            />
          </section>
        </div>

        <aside className="dashboard-secondary-rail">
          <section className="dashboard-rail-card">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Signal Rail</span>
                <h2 className="dashboard-section-title">Plattform</h2>
              </div>
            </div>
            <div className="dashboard-rail-metrics">
              {platformSignals.map((item) => (
                <article key={item.label} className="dashboard-metric-card">
                  <span className="dashboard-summary-label">{item.label}</span>
                  <strong>{item.value}</strong>
                  <p>{item.note}</p>
                </article>
              ))}
              <article className="dashboard-metric-card">
                <span className="dashboard-summary-label">Registrierte Nutzer</span>
                <strong>{summary.total || 0}</strong>
                <p>Quelle `/api/users/summary`, sichtbar aber nicht lauter als die Review-Flaeche.</p>
              </article>
              <article className="dashboard-metric-card">
                <span className="dashboard-summary-label">Health</span>
                <strong>{health.ok ? `${health.name || "Experts4Europe"} OK` : "Degraded"}</strong>
                <p>Systemsignal bleibt kompakt in der Secondary Rail.</p>
              </article>
            </div>
          </section>

          <section className="dashboard-rail-card">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Quick Paths</span>
                <h2 className="dashboard-section-title">Direkt erreichbar</h2>
              </div>
            </div>
            <div className="dashboard-action-stack">
              <button type="button" className="dashboard-stack-button is-primary" onClick={() => navigate("/dashboard/admin/users")}>Users</button>
              <button type="button" className="dashboard-stack-button" onClick={() => navigate("/dashboard/admin/assessment-ops")}>Assessment</button>
              <button type="button" className="dashboard-stack-button" onClick={() => navigate("/dashboard/security")}>Security</button>
              <button type="button" className="dashboard-stack-button" onClick={() => navigate("/dashboard/gpt")}>GPT</button>
            </div>
          </section>
        </aside>
      </div>
    </CockpitShell>
  );
};

export default AdminDashboard;
